import { useLiveQuery } from 'dexie-react-hooks'
import { api } from '../../api/endpoints'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { channelTables, db, deleteChannelData, type LocalChannel } from '../../db/db'
import { leaveAccess, type LeaveAccess } from '../../lib/managed'
import { toast } from '../../state/toasts'
import { toastChannelError } from './channelErrors'

// Leaving a group chat or channel, from its details or from the conversation list.

/** Whether I can leave the conversation, with my member row on this device deciding for managed ones. */
export function useLeaveAccess(channel: LocalChannel, meId: string): LeaveAccess {
  // undefined while loading; null when this user has no member row (e.g. an admin who has not joined).
  const membership = useLiveQuery(() => db.members.get([channel.id, meId]).then((row) => row ?? null), [channel.id, meId])
  return leaveAccess(channel, membership)
}

/** Leaves the conversation and removes it from this device. Returns whether that worked; failures are toasted. */
export async function leaveConversation(channelId: string, meId: string, name: string): Promise<boolean> {
  try {
    await api.removeMember(channelId, meId)
    await db.transaction('rw', channelTables(), () => deleteChannelData(channelId))
    toast(`You left ${name}`)
    return true
  } catch (error) {
    toastChannelError("Couldn't leave.", error)
    return false
  }
}

interface LeaveConfirmDialogProps {
  open: boolean
  name: string
  onClose: () => void
  onConfirm: () => Promise<void>
}

export function LeaveConfirmDialog({ open, name, onClose, onConfirm }: LeaveConfirmDialogProps) {
  return (
    <ConfirmDialog open={open} title={`Leave ${name}?`} confirmLabel="Leave" danger onClose={onClose} onConfirm={onConfirm}>
      <p>You'll stop receiving messages from this conversation. Someone will need to add you back to rejoin.</p>
    </ConfirmDialog>
  )
}
